import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { isPlatformServer } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';

type NodeRequireLike = {
    (moduleName: string): any;
    main?: {
        filename?: string;
    };
};
type ServerProcessLike = {
    cwd?: () => string;
};

type ServerGlobalLike = typeof globalThis & {
    __serverRequire__?: NodeRequireLike;
    process?: ServerProcessLike;
};

@Injectable({ providedIn: 'root' })
export class IconsService {
    /**
     * Constructor
     */
    constructor() {
        const domSanitizer = inject(DomSanitizer);
        const matIconRegistry = inject(MatIconRegistry);
        const platformId = inject(PLATFORM_ID);

        const registerIconSet = (namespace: string | null, assetPath: string): void => {
            if (isPlatformServer(platformId)) {
                const svgContent = this.loadServerSvgIconSet(assetPath);
                if (svgContent) {
                    const trustedSvg = domSanitizer.bypassSecurityTrustHtml(svgContent);

                    if (namespace) {
                        matIconRegistry.addSvgIconSetLiteralInNamespace(namespace, trustedSvg);
                    } else {
                        matIconRegistry.addSvgIconSetLiteral(trustedSvg);
                    }
                    return;
                }
            }

            const trustedUrl = domSanitizer.bypassSecurityTrustResourceUrl(assetPath);

            if (namespace) {
                matIconRegistry.addSvgIconSetInNamespace(namespace, trustedUrl);
            } else {
                matIconRegistry.addSvgIconSet(trustedUrl);
            }
        };

        // Register icon sets
        registerIconSet(null, '/icons/material-twotone.svg');
        registerIconSet('mat_outline', '/icons/material-outline.svg');
        registerIconSet('mat_solid', '/icons/material-solid.svg');
        registerIconSet('feather', '/icons/feather.svg');
        registerIconSet('heroicons_outline', '/icons/heroicons-outline.svg');
        registerIconSet('heroicons_solid', '/icons/heroicons-solid.svg');
        registerIconSet('heroicons_mini', '/icons/heroicons-mini.svg');
        registerIconSet('spf', '/icons/spf.svg');
    }

    private loadServerSvgIconSet(assetPath: string): string | null {
        try {
            const globalRef = globalThis as ServerGlobalLike;
            const nodeRequire = this.getServerRequire(globalRef);

            if (!nodeRequire) {
                return null;
            }

            const path = nodeRequire('path');
            const fs = nodeRequire('fs');
            const normalizedAssetPath = assetPath
                .replace(/^\//, '')
                .replace(/\//g, path.sep);
            const processRef = globalRef.process;
            const currentWorkingDirectory =
                typeof processRef?.cwd === 'function' ? processRef.cwd() : '';
            const mainFilename =
                typeof nodeRequire.main?.filename === 'string' ? nodeRequire.main.filename : '';
            const serverMainPath = mainFilename
                ? path.dirname(mainFilename)
                : '';
            const candidatePaths = [
                currentWorkingDirectory
                    ? path.join(currentWorkingDirectory, 'public', normalizedAssetPath)
                    : null,
                currentWorkingDirectory
                    ? path.join(currentWorkingDirectory, 'browser', normalizedAssetPath)
                    : null,
                currentWorkingDirectory
                    ? path.join(currentWorkingDirectory, 'dist', 'fuse', 'browser', normalizedAssetPath)
                    : null,
                currentWorkingDirectory
                    ? path.join(currentWorkingDirectory, 'deploy', 'browser', normalizedAssetPath)
                    : null,
                serverMainPath
                    ? path.resolve(serverMainPath, '..', 'browser', normalizedAssetPath)
                    : null,
                serverMainPath
                    ? path.resolve(serverMainPath, '..', '..', 'browser', normalizedAssetPath)
                    : null,
            ].filter((candidatePath): candidatePath is string => Boolean(candidatePath));

            for (const iconFilePath of candidatePaths) {
                if (fs.existsSync(iconFilePath)) {
                    return fs.readFileSync(iconFilePath, 'utf8');
                }
            }
        } catch {
            return null;
        }

        return '<svg xmlns="http://www.w3.org/2000/svg"></svg>';
    }

    private getServerRequire(globalRef: ServerGlobalLike): NodeRequireLike | null {
        if (typeof globalRef.__serverRequire__ === 'function') {
            return globalRef.__serverRequire__;
        }

        try {
            return Function(
                'return typeof require !== "undefined" ? require : undefined;'
            )() as NodeRequireLike | undefined | null;
        } catch {
            return null;
        }
    }
}
